import { Injectable, signal } from '@angular/core';
import { ViewMode } from '../utils/view-mode';
import { Chapter } from '../models/chapter';
import { BookConfig } from '../models/book-config';
import { environment } from '../../environments/environment';
import { PageEditMode } from '../utils/page-edit-modes/page-edit-mode';
import { UserData } from '../models/user-data';
import { CookieService } from 'ngx-cookie-service';
import { CommunicationService } from './communication.service';
import { SchoolClass } from '../models/school-class';

@Injectable({
    providedIn: 'root'
})
export class ApplicationStateService {
    globalVars: any;
    readonly viewModeCookieName = 'bv_view_mode';
    isMobileDropdownActive = false;
    preventSwipe = false;
    preventPanning = false;
    isProduction: boolean;

    constructor(private cookieService: CookieService,
                private communicationService: CommunicationService) {
        //@ts-ignore
        this.isProduction = import.meta.env.NG_APP_ENV === 'production';
    }

    private _bookId: number;

    get bookId(): number {
        return this._bookId;
    }

    set bookId(value: number) {
        this._bookId = value;
    }

    private _ownerId: number;

    get ownerId(): number {
        return this._ownerId;
    }

    set ownerId(value: number) {
        this._ownerId = value;
    }

    private _userBookId: number;

    get userBookId(): number {
        return this._userBookId;
    }

    set userBookId(value: number) {
        this._userBookId = value;
    }

    private _currentIndex: number;

    get currentIndex(): number {
        if (!this._currentIndex) {
            this._currentIndex = 0;
        }

        return this._currentIndex;
    }

    set currentIndex(value: number) {
        this._currentIndex = value;
    }

    private _baseUrl: string;

    get baseUrl(): string {
        return this._baseUrl;
    }

    set baseUrl(value: string) {
        this._baseUrl = value;
    }

    private _platformDomain: string;

    get platformDomain(): string {
        return this._platformDomain;
    }

    set platformDomain(value: string) {
        this._platformDomain = value;
    }

    private _bookConfig: BookConfig;

    get bookConfig(): BookConfig {
        return this._bookConfig;
    }

    set bookConfig(value: BookConfig) {
        this._bookConfig = value;
    }

    private _userData: UserData;

    get userData(): UserData {
        return this._userData;
    }

    set userData(value: UserData) {
        this._userData = value;
    }

    private _domain = 'digi.schule';

    get domain(): string {
        return this._domain;
    }

    set domain(value: string) {
        this._domain = value;
    }

    // -----------------------------------------------

    private _schoolClassList: Array<SchoolClass> = [];

    get schoolClassList(): Array<SchoolClass> {
        return this._schoolClassList;
    }

    set schoolClassList(value: Array<SchoolClass>) {
        this._schoolClassList = value;
    }

    private _currentChapter: Chapter;

    get currentChapter(): Chapter {
        return this._currentChapter;
    }

    set currentChapter(value: Chapter) {
        this._currentChapter = value;
    }

    private _currentViewMode: ViewMode;

    get currentViewMode(): ViewMode {
        return this._currentViewMode;
    }

    set currentViewMode(value: ViewMode) {
        this._currentViewMode = value;
    }

    private _isSidebarHidden = signal(true);

    get isSidebarHidden(): boolean {
        return this._isSidebarHidden();
    }

    set isSidebarHidden(value: boolean) {
        this._isSidebarHidden.set(value);
    }

    private _isPageEditable = false;

    get isPageEditable(): boolean {
        return this._isPageEditable;
    }

    set isPageEditable(value: boolean) {
        this._isPageEditable = value;
    }

    private _zoomLevel = 1;

    get zoomLevel(): number {
        return this._zoomLevel;
    }

    set zoomLevel(value: number) {
        const zoomLevel = Math.min(Math.max(value, 1), environment.maxZoomLevel);
        this._zoomLevel = zoomLevel;
    }

    private _pageEditMode: PageEditMode;

    get pageEditMode(): PageEditMode {
        return this._pageEditMode;
    }

    set pageEditMode(value: PageEditMode) {
        if (this._pageEditMode && this._pageEditMode.type === 'COPY') {
            // @ts-ignore
            this._pageEditMode.unsetSelection();
        }
        this._pageEditMode = value;
        this.communicationService.announceDetectChanges();
    }

    private _isOfflineModeEnabled = false;

    get isOfflineModeEnabled(): boolean {
        if (this.globalVars) {
            this._isOfflineModeEnabled = this.globalVars.offline;
        }

        return this._isOfflineModeEnabled;
    }

    set isOfflineModeEnabled(value: boolean) {
        this._isOfflineModeEnabled = value;
    }

    private _beforeViewModeChangeSize: number;

    get beforeViewModeChangeSize(): number {
        return this._beforeViewModeChangeSize;
    }

    set beforeViewModeChangeSize(value: number) {
        this._beforeViewModeChangeSize = value;
    }

    private _isAddHomeworkModeEnabled = false;

    get isAddHomeworkModeEnabled(): boolean {
        return this._isAddHomeworkModeEnabled;
    }

    set isAddHomeworkModeEnabled(value: boolean) {
        this._isAddHomeworkModeEnabled = value;
    }

    private _comesFromDigi4School = false;

    get comesFromDigi4School(): boolean {
        return this._comesFromDigi4School ||
            (environment.debug && new URLSearchParams(window.location.search).get('fromd4s') !== null);
    }

    get isZoomed(): boolean {
        return this.zoomLevel > 1;
    }

    /**
     * Initialize the application state.
     */
    init(): void {
        // Read the global variables from the window object
        this.globalVars = window.window['PhpVars'];
        if (this.globalVars === undefined) {
            throw new Error('Global variables not defined, aborting execution!');
        }

        this.initPlatformDomain();
        this.storeBookId();
        this.storeOwnerId();
        this.storeUserBookId();
        this.storeBaseUrl();
        this.initViewMode();
        this.setComesFromDigi4School();
        this.domain = window.location.hostname;
    }

    isScrollMode(): boolean {
        return this.currentViewMode.slug.includes('scroll');
    }

    /**
     * is actual mode mobile mode?
     *
     * @returns {boolean}
     */
    isMobileMode(): boolean {
        return this.currentViewMode.slug.toLowerCase()
            .includes('mobile');
    }

    getCurrentPageIndizes(): Array<number> {
        const pageIndizes = [];
        for (let runningIndex = this.currentIndex;
             runningIndex < this.currentIndex + this.currentViewMode.pagesToShow;
             runningIndex++
        ) {

            pageIndizes.push(runningIndex);

            if (runningIndex === 0) {
                break;
            }

        }

        return pageIndizes;
    }

    /**
     * Read and store the current book id.
     */
    private storeBookId(): void {
        this.bookId = this.globalVars.bookId;
    }

    /**
     * Read and store the owner id.
     */
    private storeOwnerId(): void {
        this.ownerId = this.globalVars.ownerId;
    }

    /**
     * Read and store the user book id.
     */
    private storeUserBookId(): void {
        if (this.globalVars) {
            this.userBookId = this.globalVars.userBookId;
        }
    }

    /**
     * Read and store the base url.
     */
    private storeBaseUrl(): void {
        this.baseUrl = this.globalVars.baseUrl;
    }

    /**
     * Inits the ViewMode
     */
    private initViewMode(): void {

        const tenYears = 10 * 365;

        if (!this.cookieService.check(this.viewModeCookieName)) {
            this.cookieService.set(
                this.viewModeCookieName,
                ViewMode.getDefaultViewMode().slug,
                tenYears,
                '/',
                undefined,
                true,
                'Strict');
        }

        const storedViewModeSlug = this.cookieService.get(this.viewModeCookieName);

        if (ViewMode.getScrollViewMode().slug === storedViewModeSlug) {
            this.currentViewMode = ViewMode.getScrollViewMode();

            return;
        }

        this.currentViewMode = ViewMode.getDefaultViewMode();
    }

    private setComesFromDigi4School(): void {
        this._comesFromDigi4School = this.globalVars.digi4SchoolOverlay;
    }

    private initPlatformDomain(): void {
        this.platformDomain = window.location.hostname.replace('www.', '');
        if (this.platformDomain === 'localhost') {
            this.platformDomain = 'digi.schule.local';
        }
    }
}
